import React, { useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { navigateToSettings, selectSettingsNav, closeSettings } from '@repo/redux/actions/ui';
import { BaseModal } from '@repo/components/modals';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { SettingsPanelHeader } from './components';
import componentForPanel, { panelTitle, subPanelTitle } from './panels';
import { fetchEnabledNotifications } from '@repo/redux/actions/user';
import { fetchReconnectNumberPerDay } from '@repo/redux/actions/userSettings';
import SettingsNav from './SettingsNav';
import styles from './Settings.module.scss';
import { useTheme } from '@repo/themes';
import { useIsProd } from '@repo/hooks';
import { fetchAllIntegrations } from '@repo/redux/actions/integrations';
import { integrationsSubSectionPanels } from './panels/Integrations/panels';
import { SettingsPanel, SettingsSubPanel } from '@repo/utils/types';

interface PanelProps {
  renderAsRoute?: boolean;
}

type PanelComponent = (props?: PanelProps) => JSX.Element;

const componentForSubPanel = (section: SettingsPanel, subSection: SettingsSubPanel) => {
  if (section === 'integrations') {
    return integrationsSubSectionPanels[subSection];
  }

  return () => null;
};

function SettingsDisplay({
  renderAsRoute,
  renderedPanel,
  renderedSubPanel,
  closeSettingsDisplay,
}: {
  renderAsRoute: boolean;
  renderedPanel: SettingsPanel;
  renderedSubPanel?: SettingsSubPanel;
  closeSettingsDisplay: () => void;
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isProd = useIsProd();
  const panelRef = useRef<HTMLDivElement>(null);

  const { title, Panel, onBack } = useMemo(() => {
    let title = panelTitle(renderedPanel);
    let Panel: PanelComponent = componentForPanel[renderedPanel];
    let onBack = undefined;

    if (renderedSubPanel) {
      title = subPanelTitle(renderedSubPanel);
      Panel = componentForSubPanel(renderedPanel, renderedSubPanel);

      if (renderAsRoute) {
        onBack = () => navigate(`/settings/${renderedPanel}`);
      } else {
        onBack = () => dispatch(navigateToSettings({ panel: renderedPanel }));
      }
    }

    if (isProd && renderedPanel === 'staging') {
      Panel = () => null;
    }

    return { title, Panel, onBack };
  }, [renderedPanel, renderedSubPanel]);

  useEffect(() => {
    dispatch(fetchEnabledNotifications());
    dispatch(fetchReconnectNumberPerDay());
    dispatch(fetchAllIntegrations());
  }, []);

  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.scrollTop = 0;
      panelRef.current.focus();
    }
  }, [renderedPanel]);

  return (
    <div className={cn(styles.settings, renderAsRoute && styles.fullScreen)}>
      <SettingsNav renderAsRoute={renderAsRoute} renderedPanel={renderedPanel} />
      <div className={styles.panelWrapper}>
        <SettingsPanelHeader
          title={title}
          onBack={onBack}
          closeSettingsDisplay={closeSettingsDisplay}
        />
        <div
          ref={panelRef}
          tabIndex={-1}
          className={cn(styles.panel, styles[renderedPanel], renderAsRoute && styles.route)}
        >
          <Panel renderAsRoute={renderAsRoute} />
        </div>
      </div>
    </div>
  );
}

export function SettingsModal({ closeModal }: { closeModal: () => void }) {
  const dispatch = useDispatch();
  const settingsNav = useSelector(selectSettingsNav);
  const [theme] = useTheme();

  return (
    <BaseModal
      isOpen={true}
      onRequestClose={closeModal}
      className={cn(styles.settingsModal, styles[theme])}
    >
      <SettingsDisplay
        renderAsRoute={false}
        renderedPanel={settingsNav.panel}
        renderedSubPanel={settingsNav.subPanel}
        closeSettingsDisplay={() => dispatch(closeSettings)}
      />
    </BaseModal>
  );
}

export function SettingsView() {
  const navigate = useNavigate();
  const { panel, subpanel } = useParams();

  return (
    <SettingsDisplay
      renderAsRoute={true}
      renderedPanel={(panel as SettingsPanel) || 'general'}
      renderedSubPanel={subpanel as SettingsSubPanel}
      closeSettingsDisplay={() => navigate('/')}
    />
  );
}
